import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import type { MetasDelDia } from '@/domain/etapas';
import { avance, formatearGramos, formatearKcal, type Totales } from '@/domain/nutricion';
import { formatearNumero } from '@/domain/numbers';
import { Barra } from './comunes';

interface Props {
  totales: Totales;
  metas: MetasDelDia;
  /** Registros a los que les falta algún macro. */
  incompletos: number;
}

const mayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);

export function textoAvance(consumido: number, meta: number | null, unidad: 'kcal' | 'g'): string {
  const a = avance(consumido, meta);
  const fmt = (n: number) =>
    unidad === 'kcal' ? formatearKcal(n) : formatearGramos(n);
  if (a.meta === null) return 'Sin meta';
  if (a.faltan !== null) return `faltan ${fmt(a.faltan)}`;
  if (a.exceso !== null) return `${fmt(a.exceso)} arriba de la meta`;
  return 'meta cumplida';
}

/** "Te quedan 1,036 kcal", "120 kcal arriba de la meta", "Meta cumplida". */
export function textoKcalRestante(consumido: number, meta: number | null): string {
  const a = avance(consumido, meta);
  if (a.meta === null) return 'Sin meta';
  if (a.faltan !== null) return `Te quedan ${formatearKcal(a.faltan)}`;
  if (a.exceso !== null) return `${formatearKcal(a.exceso)} arriba de la meta`;
  return 'Meta cumplida';
}

function Macro({
  nombre,
  nombreLargo,
  consumido,
  meta,
  sinDato,
}: {
  nombre: string;
  /** Nombre completo cuando el visible va recortado ("Carbos"). */
  nombreLargo?: string;
  consumido: number;
  meta: number | null;
  sinDato: number;
}) {
  const a = avance(consumido, meta);
  return (
    <div className="min-w-0 space-y-1.5">
      <dt className="text-[12px] text-texto-suave">
        {nombreLargo === undefined ? (
          nombre
        ) : (
          <>
            <span aria-hidden="true">{nombre}</span>
            <span className="sr-only">{nombreLargo}</span>
          </>
        )}
      </dt>
      <dd className="space-y-1.5">
        <span className="block text-[15px] font-medium tabular-nums text-texto">
          {formatearGramos(consumido)}
          {sinDato > 0 && (
            <>
              <span aria-hidden="true">*</span>
              <span className="sr-only"> (incompleto)</span>
            </>
          )}
          {meta !== null && (
            <span className="text-[12px] font-normal text-texto-suave"> / {formatearGramos(meta)}</span>
          )}
        </span>
        <Barra proporcion={a.proporcion} />
        <span className="block text-[11px] text-texto-suave">{mayuscula(textoAvance(consumido, meta, 'g'))}</span>
      </dd>
    </div>
  );
}

/**
 * Totales del día contra las metas y lo que falta (Req. 18.5, 18.10). Una
 * meta que no está definida no se muestra como cero: dice "Sin meta". Si
 * algún registro no trae un macro, el total lo marca como incompleto.
 */
export function ResumenDelDia({ totales, metas, incompletos }: Props) {
  const kcal = avance(totales.kcal, metas.kcal);
  const origen = metas.origen === 'etapa' ? 'Meta de tu plan' : 'Meta sin plan';

  return (
    <section aria-labelledby="titulo-resumen-dia" className="rounded-panel bg-superficie p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 id="titulo-resumen-dia" className="antetitulo">
          Calorías del día
        </h2>
        <span className="text-[11px] text-texto-suave">{origen}</span>
      </div>

      <p className="mt-3 tabular-nums text-texto">
        <span className="text-[34px] leading-none tracking-[-1.2px]">
          {formatearNumero(totales.kcal, { decimales: 0 })}
        </span>
        <span className="text-[14px] text-texto-suave">
          {metas.kcal === null ? ' kcal' : ` / ${formatearKcal(metas.kcal)}`}
        </span>
      </p>
      {/* El porcentaje al lado de la barra, que si no es solo decoración (0.11.0) */}
      <div className="mt-3 flex items-center gap-2.5">
        <div className="min-w-0 flex-1">
          <Barra proporcion={kcal.proporcion} pasada={kcal.exceso !== null} />
        </div>
        {kcal.proporcion !== null && (
          <span className="shrink-0 text-[12px] tabular-nums text-texto-suave">
            {Math.round(kcal.proporcion * 100)} %
          </span>
        )}
      </div>
      <p className="mt-2 text-[13px] text-texto-suave">
        {metas.faltaMantenimiento ? (
          <Link to="/mas/mantenimiento" className="inline-flex items-center gap-1 text-marca underline-offset-2">
            Falta el mantenimiento para saber tu meta de kcal
            <ChevronRight className="size-3.5" />
          </Link>
        ) : metas.kcal === null ? (
          <Link to="/mas/plan" className="inline-flex items-center gap-1 text-marca">
            Sin meta de kcal: define tu plan o tus metas
            <ChevronRight className="size-3.5" />
          </Link>
        ) : (
          textoKcalRestante(totales.kcal, metas.kcal)
        )}
      </p>

      <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-borde pt-4">
        <Macro nombre="Proteína" consumido={totales.proteinaG} meta={metas.proteinaG} sinDato={totales.sinDato.proteinaG} />
        <Macro
          nombre="Carbos"
          nombreLargo="Carbohidratos"
          consumido={totales.carbohidratosG}
          meta={metas.carbohidratosG}
          sinDato={totales.sinDato.carbohidratosG}
        />
        <Macro nombre="Grasa" consumido={totales.grasaG} meta={metas.grasaG} sinDato={totales.sinDato.grasaG} />
      </dl>

      {/* De dónde salen los macros de hoy, si no los puso la etapa (0.5.1) */}
      {metas.macrosDeMantenimiento && (
        <p className="mt-3 text-[12px] leading-relaxed text-texto-suave">
          Los macros de hoy salen de tu mantenimiento, porque esta etapa no define los suyos.{' '}
          <Link to="/mas/mantenimiento" className="font-medium text-marca underline underline-offset-2">
            Revisarlos
          </Link>
        </p>
      )}

      {incompletos > 0 && (
        <p className="mt-4 text-[12px] leading-relaxed text-aviso">
          * Datos incompletos: {incompletos === 1 ? '1 registro no tiene' : `${incompletos} registros no tienen`}{' '}
          todos los macros, así que esos totales pueden ser mayores.
        </p>
      )}
    </section>
  );
}
